import { Button } from '@design/components/button/Button';
import { ThreeDots } from '@design/components/three-dots/ThreeDots';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { ThemeOverviewContent } from '@terminal/widgets/theme-widget/theme-overview-content/ThemeOverviewContent';
import { useTranslation } from 'react-i18next';

import styles from './ThemeOverviewPreviewCard.module.scss';

export interface ThemeOverviewPreviewCardProps {
  themeId: string;
}

export const ThemeOverviewPreviewCard = ({
  themeId,
}: ThemeOverviewPreviewCardProps) => {
  const { t } = useTranslation(['chat', 'widget']);
  const { data: theme, isLoading, isError } = useTheme({ themeId });
  const { navigateToTheme } = useNavigateToThemeOverview();

  if (isLoading) {
    return (
      <div
        className={styles.container}
        data-testid="theme-overview-preview-card-loading"
      >
        <div className={styles.stateContainer}>
          <ThreeDots />
        </div>
      </div>
    );
  }

  if (isError || !theme) {
    return (
      <div
        className={styles.container}
        data-testid="theme-overview-preview-card-error"
      >
        <div className={styles.stateContainer}>
          <p className={styles.emptyText}>{t('chat:empty')}</p>
        </div>
      </div>
    );
  }

  const handleExpand = () => {
    navigateToTheme({
      themeId: theme.id,
      source: POSTHOG_TRACKING_SOURCES.ALFRED_THEME_PREVIEW,
    });
  };

  return (
    <div className={styles.container} data-testid="theme-overview-preview-card">
      <WebOnlyFeat>
        <div className={styles.header}>
          <Button
            iconName="Expand"
            label={t('widget:themeOverview.drawer.themeOverview')}
            onClick={handleExpand}
            variant="empty"
            size="xsmall"
            data-testid="theme-overview-preview-card-expand"
          />
        </div>
      </WebOnlyFeat>
      <div className={styles.body}>
        <ErrorBoundaryWrapper resetKeys={[theme.id]}>
          <div className={styles.wrapper}>
            <ThemeOverviewContent
              theme={{ theme }}
              source={POSTHOG_TRACKING_SOURCES.ALFRED_THEME_PREVIEW}
              title={theme.name}
              tooltipThemeId={theme.id}
            />
          </div>
        </ErrorBoundaryWrapper>
      </div>
    </div>
  );
};
